import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Highcharts from "highcharts/highmaps";
import HighchartsReact from "highcharts-react-official";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsExportingOffline from "highcharts/modules/offline-exporting";
import HighchartsTiledWebMap from "highcharts/modules/tiledwebmap";
import mapData from "@highcharts/map-collection/countries/us/custom/us-all-territories.topo.json";
import { AgencyCoverageData } from "../../types";
import { states } from "../../data/impactData";

HighchartsExporting(Highcharts);
HighchartsExportingOffline(Highcharts);
HighchartsTiledWebMap(Highcharts);

interface AgencyCoverageProps {
  data: AgencyCoverageData;
  setMapState?: (state: string) => void;
  mapState: string;
  isExport?: boolean;
  hideFilter?: boolean;
}

const AgencyCoverage: React.FC<AgencyCoverageProps> = ({
  data,
  setMapState,
  mapState,
  isExport = false,
  hideFilter = false,
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const getMarkerColor = (type: string) => {
    switch (type) {
      case "CAC":
        return "#562a75";
      case "Prosecutor":
        return "#006fa7";
      case "Law Enforcement":
        return "#002169";
      default:
        return "#562a75";
    }
  };

  const defaultChartOptions: Highcharts.Options = {
    chart: {
      backgroundColor: "transparent",
      height: isExport ? 230 : 500,
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },
    mapNavigation: {
      enabled: isExport ? false : true,
      enableMouseWheelZoom: true,
      enableDoubleClickZoom: true,
      buttonOptions: {
        verticalAlign: "bottom",
      },
    },
    mapView: !isExport
      ? {
          fitToGeometry: {
            type: "Polygon",
            coordinates: [
              [
                [-180, 5], // Southwest corner
                [-180, 35], // Northwest corner
                [-24, 35], // Northeast corner
                [-24, 5], // Southeast corner
                [-180, 5], // Closing the polygon
              ],
            ],
          },
          center: [-102, 50],
          zoom: 2,
          padding: 5,
        }
      : {},
    series: [
      !isExport
        ? {
            type: "tiledwebmap",
            provider: {
              type: "OpenStreetMap",
              theme: "Standard",
            },
            showInLegend: false,
          }
        : {
            type: "map",
            name: "States",
            mapData: mapData,
            data: mapData.objects.default.geometries.map((g: any) => {
              return g.properties["hc-key"] === mapState
                ? {
                    "hc-key": g.properties["hc-key"],
                    id: g.properties["hc-key"],
                    value: 1,
                  }
                : {
                    "hc-key": g.properties["hc-key"],
                    id: g.properties["hc-key"],
                    value: 1,
                  };
            }),
            joinBy: "hc-key",
            showInLegend: false,
            color: "#eee",
            borderColor: "#aaa",
            borderWidth: 1,
            nullColor: "transparent",
            enableMouseTracking: false,
          },
      {
        type: "mappoint",
        data: data.centers.map((center) => ({
          lat: center.lat,
          lon: center.lng,
          color: {
            radialGradient: { cx: 0.5, cy: 0.5, r: 0.5 },
            stops: [
              [0, getMarkerColor(center.type)],
              [0.7, getMarkerColor(center.type)],
              [0.7, "white"],
            ],
          },
        })),
        marker: {
          radius: 4,
        },
        enableMouseTracking: false,
      },
    ],
    legend: {
      enabled: false,
    },
  };

  const [chartOptions, setChartOptions] =
    useState<Highcharts.Options>(defaultChartOptions);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart) {
      chart.redraw();
    }
  }, [chartOptions]);

  useEffect(() => {
    const chart = chartRef.current?.chart as any;
    if (!chart || !mapState || mapState === "All States") {
      chart.mapView.setView([-102, 50], 2);
      return;
    }

    const state = states.find((state) => state.value === mapState);
    if (mapState === "us-pr") {
      if (isExport) {
        chart.mapView.setView([-92, 10], 5.5);
      } else {
        chart.mapView.setView([-66.48, 18.23], 7.5);
      }
    } else if (state?.center) {
      if (isExport) {
        const center = state?.exportCenter || state?.center;
        const zoom = state?.exportZoom || state?.zoom;
        chart.mapView.setView([center[0], center[1]], zoom - 1);
      } else {
        chart.mapView.setView(
          [state?.center[0], state?.center[1]],
          state?.zoom
        );
      }
    }
  }, [mapState]);

  return (
    <Card>
      <CardContent
        className={isExport ? "export-card-content" : ""}
        sx={{ p: isExport ? "2px 8px" : 3 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: isExport ? 0 : 3,
          }}
        >
          <Box sx={{ width: isExport ? "100%" : "auto" }}>
            <Typography
              variant={isExport ? "body1" : "h5"}
              component="h2"
              sx={{
                mb: 1,
                fontWeight: 600,
                textAlign: isExport ? "center" : "left",
              }}
            >
              Agency Coverage Map
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ display: isExport ? "none" : "block" }}
            >
              Guardify's impact spans across supporting Child Advocacy Centers
              nationwide.
            </Typography>
          </Box>

          {!hideFilter && setMapState ? (
            <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Filter by</InputLabel>
              <Select
                value={mapState}
                onChange={(e) => setMapState(e.target.value)}
                label="Filter by"
              >
                <MenuItem value="All States">All States</MenuItem>
                <hr />
                {states.slice(0).map((state) => (
                  <MenuItem key={state.name} value={state.value}>
                    {state.name}
                  </MenuItem>
                ))}
                <hr />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, textAlign: "center" }}
                >
                  U.S. Territories
                </Typography>
                <hr />
                <MenuItem value="us-pr">Puerto Rico</MenuItem>
              </Select>
            </FormControl>
          ) : null}
        </Box>

        {/* Placeholder for map - In production, use actual Highcharts Maps */}
        <Box
          id="map-container"
          sx={{
            height: isExport ? 230 : 500,
            width: "100%",
            bgcolor: "#F8FAFC",
            border: "2px solid #CBD5E1",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <HighchartsReact
            id="map"
            ref={chartRef}
            highcharts={Highcharts}
            constructorType="mapChart"
            options={chartOptions}
            containerProps={{ style: { width: "100%", height: "100%" } }}
          />
          {/* Map legend */}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "background.paper",
              p: 2,
              borderRadius: 1,
              boxShadow: 1,
              display: isExport ? "none" : "block",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Legend
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: "#562a75",
                    borderRadius: "50%",
                  }}
                />
                <Typography variant="caption">CAC</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: "#006fa7",
                    borderRadius: "50%",
                  }}
                />
                <Typography variant="caption">Prosecutors</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: "#002169",
                    borderRadius: "50%",
                  }}
                />
                <Typography variant="caption">Law Enforcement</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: isExport ? "flex" : "none",
            width: "100%",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            mt: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, height: "100%" }}>
            Legend:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: "#6B46C1",
                  borderRadius: "50%",
                }}
              />
              <Typography variant="caption">CAC</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: "#016fa7",
                  borderRadius: "50%",
                }}
              />
              <Typography variant="caption">Prosecutors</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: "#00216a",
                  borderRadius: "50%",
                }}
              />
              <Typography variant="caption">Law Enforcement</Typography>
            </Box>
          </Box>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 2,
            textAlign: "center",
            display: isExport ? "none" : "block",
          }}
        >
          Interactive map showing Guardify Usage across the North America
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AgencyCoverage;
